import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FlaskConical } from "lucide-react";
import { useOrgOptics } from "@/hooks/useResults";
import { useDevices } from "@/hooks/useDevices";
import { qpcrApi } from "@/api/qpcr";
import {
  PageHeader, DataTable, EmptyState,
  FilterBar, SearchInput, FilterSelect, type Column,
} from "@/components/ui";
import type { OrgOptic } from "@/api/results";

const RANGES = [
  { value: "0", label: "All time" },
  { value: "1", label: "Last 24h" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
];

// Cross-fleet Results explorer. When `deviceId` is provided it powers the
// device-detail Results tab (pre-filtered to that device).
export function ResultsExplorer({ deviceId }: { deviceId?: string }) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useOrgOptics(300);
  const devicesQ = useDevices();
  const pathogensQ = useQuery({
    queryKey: ["pathogens"],
    queryFn: () => qpcrApi.pathogens(),
    staleTime: 5 * 60_000,
  });

  const [search, setSearch] = useState("");
  const [deviceFilter, setDeviceFilter] = useState(deviceId ?? "");
  const [pathogenFilter, setPathogenFilter] = useState("");
  const [rangeDays, setRangeDays] = useState("0");

  const pathogenName = useMemo(() => {
    const m = new Map<number, string>();
    (pathogensQ.data?.pathogens ?? []).forEach((p) => m.set(p.id, p.name));
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [pathogensQ.data]);

  const deviceOptions = useMemo(
    () => [
      { value: "", label: "All devices" },
      ...(devicesQ.data?.devices ?? []).map((d) => ({
        value: d.id,
        label: d.label ?? d.serial_number,
      })),
    ],
    [devicesQ.data],
  );

  const pathogenOptions = useMemo(
    () => [
      { value: "", label: "All pathogens" },
      ...(pathogensQ.data?.pathogens ?? []).map((p) => ({
        value: String(p.id),
        label: p.name,
      })),
    ],
    [pathogensQ.data],
  );

  const rows = useMemo(() => {
    let list = (data?.optics ?? []).filter((o) => !o.hidden);
    if (deviceId) list = list.filter((o) => o.device_id === deviceId);
    else if (deviceFilter) list = list.filter((o) => o.device_id === deviceFilter);
    if (pathogenFilter) list = list.filter((o) => o.pathogen_ids.includes(Number(pathogenFilter)));
    if (rangeDays !== "0") {
      const cutoff = Date.now() - Number(rangeDays) * 86_400_000;
      list = list.filter((o) => new Date(o.received_at).getTime() >= cutoff);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((o) =>
        (o.device_label ?? "").toLowerCase().includes(q) ||
        (o.device_serial ?? "").toLowerCase().includes(q) ||
        o.pathogen_ids.some((pid) => pathogenName(pid).toLowerCase().includes(q)),
      );
    }
    return list;
  }, [data, deviceId, deviceFilter, pathogenFilter, rangeDays, search, pathogenName]);

  const columns: Column<OrgOptic>[] = [
    {
      key: "received_at",
      header: "Time",
      sortable: true,
      sortValue: (o) => o.received_at,
      render: (o) => new Date(o.received_at).toLocaleString(),
      numeric: true,
    },
    ...(deviceId
      ? []
      : [{
          key: "device",
          header: "Device",
          sortable: true,
          sortValue: (o: OrgOptic) => o.device_label ?? o.device_serial ?? "",
          render: (o: OrgOptic) => o.device_label ?? o.device_serial ?? "—",
        } as Column<OrgOptic>]),
    {
      key: "pathogens",
      header: "Targets",
      render: (o) =>
        o.pathogen_ids.length === 0
          ? <span className="text-textLight">—</span>
          : (
            <span className="flex flex-wrap gap-xs">
              {o.pathogen_ids.map((pid) => (
                <span key={pid} className="inline-flex items-center rounded-sm bg-purple-bg text-purple-text px-sm py-[2px] text-xs">
                  {pathogenName(pid)}
                </span>
              ))}
            </span>
          ),
    },
    {
      key: "dump",
      header: "Raw",
      align: "center",
      render: (o) => o.has_dump
        ? <span className="text-success-text text-xs">●</span>
        : <span className="text-textLight text-xs">—</span>,
    },
  ];

  const body = (
    <>
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search device / pathogen…" />
        {!deviceId && (
          <FilterSelect value={deviceFilter} onChange={setDeviceFilter} options={deviceOptions} label="Device" />
        )}
        <FilterSelect value={pathogenFilter} onChange={setPathogenFilter} options={pathogenOptions} label="Pathogen" />
        <FilterSelect value={rangeDays} onChange={setRangeDays} options={RANGES} label="Range" />
      </FilterBar>
      {error ? (
        <EmptyState title="Could not load results" icon={FlaskConical} />
      ) : !isLoading && rows.length === 0 ? (
        <EmptyState title="No results match" hint="Adjust the filters above." icon={FlaskConical} />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          getRowKey={(o) => o.id}
          loading={isLoading}
          onRowClick={(o) => navigate(`/devices/${o.device_id}/results/${o.id}`)}
          initialSort={{ key: "received_at", dir: "desc" }}
        />
      )}
    </>
  );

  // Embedded (device tab): no page header. Standalone: full page.
  if (deviceId) return <div className="space-y-md">{body}</div>;

  return (
    <div className="space-y-md">
      <PageHeader title="Results" subtitle="qPCR runs across your fleet" icon={FlaskConical} />
      {body}
    </div>
  );
}

export default ResultsExplorer;
